import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Sun, CloudSun, Cloud, CloudRain, CloudLightning, CloudSnow, Wind, Umbrella,
  SunHorizon, MoonStars, WarningCircle, Broadcast,
} from "@phosphor-icons/react";

/**
 * Map Open-Meteo WMO codes to Phosphor icon + FR label + accent color.
 * https://open-meteo.com/en/docs#weather_variable_documentation
 */
function decodeCode(code) {
  if (code === 0) return { Icon: Sun, label: "Ciel dégagé", color: "#F59E0B" };
  if (code === 1 || code === 2) return { Icon: CloudSun, label: "Peu nuageux", color: "#FBBF24" };
  if (code === 3) return { Icon: Cloud, label: "Couvert", color: "#9CA3AF" };
  if (code >= 45 && code <= 48) return { Icon: Cloud, label: "Brouillard", color: "#94A3B8" };
  if (code >= 51 && code <= 67) return { Icon: CloudRain, label: "Pluie", color: "#38BDF8" };
  if (code >= 71 && code <= 77) return { Icon: CloudSnow, label: "Neige", color: "#E5E7EB" };
  if (code >= 80 && code <= 82) return { Icon: CloudRain, label: "Averses", color: "#0EA5E9" };
  if (code >= 95) return { Icon: CloudLightning, label: "Orage", color: "#8B5CF6" };
  return { Icon: Cloud, label: "—", color: "#9CA3AF" };
}

const FR_DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

export default function WeatherWidget() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("today"); // today | week
  const [alert, setAlert] = useState(null);
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/weather/today");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Météo indisponible");
      }
      if (isAdmin) {
        try {
          const a = await api.get("/weather/rain-alert");
          if (alive && a.data?.threshold_hit) setAlert(a.data);
        } catch (err) { console.debug("[weather] rain-alert fetch failed:", err?.message || err); }
      }
    })();
    return () => { alive = false; };
  }, [isAdmin]);

  const notifyTeam = async () => {
    setNotifying(true);
    try {
      const r = await api.post("/weather/notify-rain");
      if (r.data?.sent > 0) toast.success(`Alerte envoyée à ${r.data.sent} technicien(s)`);
      else toast.info(r.data?.reason || "Aucun tech concerné");
      setAlert(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setNotifying(false); }
  };

  if (error || data?.unavailable) return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 text-[11px] text-white/50" data-testid="weather-widget-error">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Météo</div>
      <div className="text-white/70">Météo temporairement indisponible</div>
      <div className="text-white/40 mt-1">Le service Open-Meteo est momentanément inaccessible. Réessayez dans quelques minutes.</div>
    </div>
  );
  if (!data) return <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 h-40 animate-pulse" data-testid="weather-widget-loading" />;

  const cur = data.current || {};
  const daily = data.daily || {};
  const hourly = data.hourly || {};
  const meta = decodeCode(cur.weather_code);
  const tempMax = daily.temperature_2m_max?.[0];
  const tempMin = daily.temperature_2m_min?.[0];
  const rainProb = daily.precipitation_probability_max?.[0] ?? 0;
  const sunrise = daily.sunrise?.[0] ? new Date(daily.sunrise[0]).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : null;
  const sunset = daily.sunset?.[0] ? new Date(daily.sunset[0]).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : null;
  const city = data.location?.city || "";

  const times = hourly.time || [];
  const temps = hourly.temperature_2m || [];
  const codes = hourly.weather_code || [];
  const probs = hourly.precipitation_probability || [];
  const nowIdx = Math.max(0, times.findIndex((t) => new Date(t).getTime() >= Date.now()));
  const hours = times.slice(nowIdx, nowIdx + 6).map((t, i) => ({ time: t, temp: temps[nowIdx + i], code: codes[nowIdx + i], prob: probs[nowIdx + i] }));

  const dTimes = daily.time || [];
  const dMax = daily.temperature_2m_max || [];
  const dMin = daily.temperature_2m_min || [];
  const dCodes = daily.weather_code || [];
  const dProbs = daily.precipitation_probability_max || [];
  const days5 = dTimes.slice(0, 5).map((t, i) => ({ date: t, code: dCodes[i], max: dMax[i], min: dMin[i], prob: dProbs[i] ?? 0 }));

  return (
    <div className="rounded-2xl bg-gradient-to-br from-sky-500/10 via-white/[0.02] to-amber-500/[0.05] border border-white/10 p-5 space-y-3" data-testid="weather-widget">
      {/* Rain alert banner */}
      {alert && (
        <div className="rounded-xl bg-amber-500/15 border border-amber-500/40 p-3 flex items-start gap-2" data-testid="weather-rain-alert">
          <WarningCircle size={16} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-amber-300">Pluie prévue demain ({alert.rain_probability}%)</div>
            <div className="text-[10px] text-amber-200/70">{alert.outdoor_iv_count} intervention(s) extérieure(s) · {alert.concerned_tech_ids.length} tech(s) concerné(s)</div>
          </div>
          {isAdmin && alert.concerned_tech_ids.length > 0 && (
            <button
              onClick={notifyTeam}
              disabled={notifying}
              className="text-[10px] px-2 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center gap-1 shrink-0 disabled:opacity-50"
              data-testid="weather-notify-team"
            >
              <Broadcast size={10} weight="bold" /> {notifying ? "…" : "Prévenir"}
            </button>
          )}
        </div>
      )}

      {/* Header + tab toggle */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Météo · {city}</div>
        </div>
        <div className="flex rounded-full bg-white/5 border border-white/10 p-0.5" data-testid="weather-tabs">
          {[{ v: "today", label: "Auj." }, { v: "week", label: "5 j." }].map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors ${tab === t.v ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
              data-testid={`weather-tab-${t.v}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {tab === "today" ? (
        <>
          {/* Current temp */}
          <div className="flex items-end gap-3">
            <div className="font-display text-5xl font-bold tabular-nums leading-none" data-testid="weather-current-temp">{Math.round(cur.temperature_2m ?? 0)}°</div>
            <div className="pb-1 flex-1 min-w-0">
              <div className="text-[11px] text-white/80 truncate">{meta.label}</div>
              <div className="text-[10px] text-white/40 flex items-center gap-2">
                <span>↑ {Math.round(tempMax ?? 0)}°</span><span>↓ {Math.round(tempMin ?? 0)}°</span>
              </div>
            </div>
            <meta.Icon size={40} weight="duotone" style={{ color: meta.color }} data-testid="weather-current-icon" />
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/5">
            <div className="text-center py-1">
              <Umbrella size={13} className="mx-auto text-sky-400" weight="duotone" />
              <div className="text-[11px] tabular-nums mt-0.5" data-testid="weather-rain-prob">{rainProb}%</div>
              <div className="text-[8px] uppercase tracking-widest text-white/40">Pluie</div>
            </div>
            <div className="text-center py-1">
              <Wind size={13} className="mx-auto text-white/60" weight="duotone" />
              <div className="text-[11px] tabular-nums mt-0.5">{Math.round(cur.wind_speed_10m ?? 0)} km/h</div>
              <div className="text-[8px] uppercase tracking-widest text-white/40">Vent</div>
            </div>
            <div className="text-center py-1">
              <SunHorizon size={13} className="mx-auto text-amber-400" weight="duotone" />
              <div className="text-[11px] tabular-nums mt-0.5">{sunrise || "—"}</div>
              <div className="text-[8px] uppercase tracking-widest text-white/40">Lever</div>
            </div>
          </div>

          {/* Hourly forecast strip */}
          {hours.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <div className="grid grid-cols-6 gap-1" data-testid="weather-hourly">
                {hours.map((h, i) => {
                  const hm = decodeCode(h.code);
                  return (
                    <div key={h.time} className="text-center rounded-lg py-1.5 hover:bg-white/[0.03]">
                      <div className="text-[9px] text-white/50 tabular-nums">{new Date(h.time).toLocaleTimeString("fr-FR", { hour: "2-digit" })}</div>
                      <hm.Icon size={14} className="mx-auto my-0.5" weight="duotone" style={{ color: hm.color }} />
                      <div className="text-[10px] font-mono tabular-nums">{Math.round(h.temp ?? 0)}°</div>
                      {h.prob > 30 && <div className="text-[8px] text-sky-400 tabular-nums">{h.prob}%</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {sunset && (
            <div className="pt-1 flex items-center justify-end gap-1 text-[9px] text-white/40">
              <MoonStars size={10} weight="duotone" /> Coucher {sunset}
            </div>
          )}
        </>
      ) : (
        /* 5-day forecast */
        <div className="space-y-1.5 pt-1" data-testid="weather-week">
          {days5.map((d, i) => {
            const dm = decodeCode(d.code);
            const dt = new Date(d.date);
            const isToday = i === 0;
            const isTomorrow = i === 1;
            const dayLabel = isToday ? "Aujourd'hui" : isTomorrow ? "Demain" : `${FR_DAYS[dt.getDay()]} ${dt.getDate()}`;
            return (
              <div
                key={d.date}
                className={`flex items-center gap-3 py-2 px-2 rounded-lg ${d.prob >= 60 ? "bg-sky-500/10 border border-sky-500/25" : "bg-white/[0.02]"}`}
                data-testid={`weather-day-${i}`}
              >
                <div className="w-20 text-xs font-semibold shrink-0">{dayLabel}</div>
                <dm.Icon size={20} weight="duotone" style={{ color: dm.color }} className="shrink-0" />
                <div className="text-[10px] text-white/50 flex-1 truncate">{dm.label}</div>
                {d.prob > 0 && (
                  <div className={`text-[10px] tabular-nums flex items-center gap-0.5 shrink-0 ${d.prob >= 60 ? "text-sky-300 font-semibold" : "text-sky-400/70"}`}>
                    <Umbrella size={10} weight="fill" /> {d.prob}%
                  </div>
                )}
                <div className="text-xs font-mono tabular-nums shrink-0">
                  <span className="text-white">{Math.round(d.max ?? 0)}°</span>
                  <span className="text-white/40"> / {Math.round(d.min ?? 0)}°</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
